import { IsEmail, IsNotEmpty, IsNumber, MinLength } from 'class-validator';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Component } from 'react';
import { Link, Redirect, RouteComponentProps } from 'react-router-dom';
import { UserApi } from '@intelingu/core/api/UserApi';
import { COMMON_NS } from '@intelingu/core/constants/base.constants';
import { IsEqualTo } from '@intelingu/core/validators/decorators/IsEqualTo';
import { lazyInject } from '@intelingu/core/IoC/container';
import { CourseStore } from '@intelingu/core/stores/CourseStore';
import { UserStore } from '@intelingu/core/stores/UserStore';
import Logger from '@intelingu/core/utils/Logger';
import withTranslation from '@intelingu/core/utils/withTranslation';
import { CoursesSelect } from '../../../partials/CoursesSelect';
import { SubmitButton } from '../../../partials/form/elements/SubmitButton';
import { TextInput } from '../../../partials/form/elements/TextInput';
import { Field } from '@intelingu/core/components/form/Field';
import { Form } from '@intelingu/core/components/form/Form';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { RouterProps } from '../../../Router';
import {AuthStore} from "../../../../stores/AuthStore";

interface OAuthPageProps extends RouteComponentProps<any> {
    t?;
}

class RegisterRequest {

    @IsEmail()
    email: string;

    @IsNumber()
    course: number;

    @IsNotEmpty()
    first_name: string;

    @IsNotEmpty()
    last_name: string;

}

@withTranslation(COMMON_NS)
@observer export class OAuthPage extends Component<OAuthPageProps, any> {

    @lazyInject(UserApi) userApi: UserApi;
    @lazyInject(UserStore) userStore: UserStore;
    @lazyInject(CourseStore) courseStore: CourseStore;
    @lazyInject(AuthStore) authStore: AuthStore;

    isApp = false;

    @observable isRegisterError = false;
    @observable confirm = false;
    @observable error = null;
    @observable formData: any = {};

    getParam(name) {
        const result = location.search
            .split('&')
            .map(value => {
                const v = value.split(`${name}=`);

                if (v.length > 1) {
                    return v[1];
                }

                return null
            })
            .filter(v => v);

        if (result.length) {
            return result[0];
        }

        return null;
    }

    async componentDidMount() {
        const { match } = this.props;

        this.isApp = match.params.app === 'app';

        const provider = match.params.provider;
        const code = this.getParam('code');
        const state = this.getParam('state');

        if (!code || !state) {
            return null;
        }

        console.log({ provider, code, state });

        try {
            const response = await this.userApi.socialAuth(provider + (this.isApp ? '/app' : ''), code, state);

            console.log('auth response', response);

            if (response.data.confirm_token) {
                console.log('load courses list');
                await this.courseStore.loadCoursesList(true);

                this.formData = response.data;
                this.confirm = true;

                return;
            }

            if (response.data.token) {
                if (this.isApp) {
                    window.location.href = 'intelingu://token/' + response.data.token;
                    return;
                }

                console.log('auth with token', response.data.token);

                this.authStore.authorizeWithToken(response.data.token);
            }
        } catch (e) {
            this.error = e.response.data.name;
            console.log(e);
        }
    }

    @action.bound async register(data, done) {
        try {
            const response = await this.userApi.register({
                ...data,
                token: this.formData.confirm_token
            });

            if (this.isApp) {
                window.location.href = 'intelingu://token/' + response.data.token;
                return;
            }

            this.authStore.authorizeWithToken(response.data.token);
        } catch (e) {
            this.isRegisterError = true;

            Logger.error(e);
        } finally {
            done();
        }
    }

    render() {
        const { t } = this.props;

        if (this.userStore.user) {
            return <Redirect to={`/`} />
        }

        if (this.confirm) {
            return <div className={'session-page'}>
                <h1>{ t('socialAuthConfirmDataTitle') }</h1>

                <Form
                    validator={RegisterRequest}
                    defaultData={this.formData.user}
                    onSubmit={this.register}
                >
                    <FormGroup>
                        <Label for="email">{ t('sessionEmail') }</Label>

                        <Field
                            name={'email'}
                            render={props => <TextInput type={'email'} {...props} />}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="course">{ t('registerTrialCourse') }</Label>

                        <Field
                            name={'course'}
                            render={props => {
                                return <CoursesSelect addOther={true} defaultValue={props.value} onChange={props.onChange} />
                            }}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="first_name">{ t('profileFormFirstName') }</Label>

                        <Field
                            name={'first_name'}
                            render={props => <TextInput {...props} />}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="last_name">{ t('profileFormLastName') }</Label>

                        <Field
                            name={'last_name'}
                            render={props => <TextInput {...props} />}
                        />
                    </FormGroup>

                    { this.isRegisterError && (
                        <div className="alert alert-danger" style={{ display: 'block' }}>{ t('sessionEmailAlreadyUsed') }</div>
                    )}

                    <FormGroup style={{ marginTop: 20 }}>
                        <Field submit render={props => <SubmitButton className={'outline'} {...props}>{ t('sessionRegisterButton') }</SubmitButton>} />
                    </FormGroup>
                </Form>
            </div>
        }

        return <div className={'static-page'}>
            { !this.error && (
                <>
                    <h1 className={'mt-3 text-center'}>{ t('socialAuthProcessing') }</h1>

                    <p className={'text-center'}>
                        { t('socialAuthRedirectingMessage') }
                    </p>
                </>
            )}

            { this.error && (
                <>
                    <div className="alert alert-danger" style={{ display: 'block' }}>{ t(`socialAuth${this.error}`) }</div>
                </>
            )}
        </div>
    }

}
